import { type CaptureFileJSONObject, IkeFieldType } from '@ikegps/core-api';
import type { FormModelField } from '../../form/form.field';
import { CollectionModelField, ModelMutator } from '../collection.field';
import { registerFieldType } from './field.registry';
import type { CaptureProvider } from './provider/capture.provider';

export class ModelMutatorTrueSize extends ModelMutator<FormModelField, CollectionModelFieldTrueSize> {
	add(captureId: string) {
		const stateValue = this.field.stateValue != null ? this.field.stateValue.slice() : [];
		if (stateValue.find((cId) => captureId === cId)) {
			return;
		}
		stateValue.push(captureId);
		this.state.setFieldValue(this.field.id, stateValue);
	}

	override copy(from: CollectionModelFieldTrueSize): boolean {
		for (const capture of from.getCaptures()) {
			this.add(capture.objectId);
		}
		return true;
	}

	override clear() {
		const collection = this.field.collection;
		const usedCaptures = this.getUsedCaptures();
		for (const capture of this.field.getCaptures()) {
			const drawableState = collection.getDrawableState(capture.objectId);

			// Don't remove markers if TS capture is used by another field
			if (drawableState != null && !usedCaptures[capture.objectId]) {
				drawableState.clear();
			}
		}

		this.state.setFieldValue(this.field.id, null);
	}

	private getUsedCaptures(): { [captureId: string]: boolean } {
		const usedCaptures: { [captureId: string]: boolean } = {};
		const captureIds = this.state.getFieldValue(this.field.id) as string[];
		if (captureIds == null || captureIds.length === 0) {
			return usedCaptures;
		}

		const collection = this.field.collection;
		collection.forEachField((currentField) => {
			if (!CollectionModelFieldTrueSize.isTrueSizeField(currentField) || currentField.id === this.field.id) {
				return;
			}

			const currentCaptures = currentField.getCaptures();
			for (const currentCapture of currentCaptures) {
				if (captureIds.indexOf(currentCapture.objectId) !== -1) {
					usedCaptures[currentCapture.objectId] = true;
				}
			}
		});

		return usedCaptures;
	}
}

export type CollectionStateTrueSize = string[];
export class CollectionModelFieldTrueSize
	extends CollectionModelField<FormModelField, CollectionStateTrueSize>
	implements CaptureProvider
{
	static TYPE: IkeFieldType = IkeFieldType.TRUE_SIZE;
	static isTrueSizeField(f: CollectionModelField): f is CollectionModelFieldTrueSize {
		return f.schema.type === CollectionModelFieldTrueSize.TYPE;
	}

	private _Mutator: ModelMutatorTrueSize;
	get Mutator() {
		if (this._Mutator == null) {
			this._Mutator = new ModelMutatorTrueSize(this);
		}
		return this._Mutator;
	}

	override get value(): CaptureFileJSONObject[] {
		const captureIds = this.stateValue || [];
		const output: CaptureFileJSONObject[] = [];
		for (const captureId of captureIds) {
			const capture = this.collection.state.getCapture(captureId);
			if (capture == null) {
				throw new Error(`Field "${this.id}": Cannot find capture ${captureId}`);
			}
			output.push(capture);
		}
		return output;
	}

	getOwnedCaptures(): CaptureFileJSONObject[] {
		return this.getCaptures();
	}

	getCaptures() {
		return this.getCaptureIds()
			.map((c) => this.collection.state.getCapture(c))
			.filter((c) => !!c); // Handle fetchable returning "null" when race conditions occur
	}

	getOwnedCaptureIds(): string[] {
		return this.getCaptureIds();
	}
	getCaptureIds(): string[] {
		const stateValue = this.stateValue;
		if (stateValue == null) {
			return [];
		}
		return this.stateValue;
	}

	hasValue(): boolean {
		return this.value.length > 0;
	}
}

registerFieldType(IkeFieldType.TRUE_SIZE, (parent, schema) => new CollectionModelFieldTrueSize(parent, schema));
